import { CopyButton, PageSection, PageWrapper } from '@src/components';
import {
  useGetQRCodeDataQuery,
  useGetQRCodeQuery,
} from '@src/services/esim/esimApiSlice';
import { Button } from 'antd';
import QRCode from './QRCode';
import { useGetPackageQuery } from '@src/services/packages/packageApiSlice';
import { PackageCard } from '../../Package';
import { ResponsiveModal } from '@src/components/ResponsiveModal';
import { useTranslation } from 'react-i18next';
export type QRCodeViewProps = {
  esimId: string;
  packageId?: string;
  isOpen: boolean;
  onClose: () => void;
};
export function QRCodeView({
  esimId,
  packageId,
  isOpen,
  onClose,
}: Readonly<QRCodeViewProps>) {
  const { t } = useTranslation();
  const { data: QRCodeData, isLoading: isQRCodeDataLoading } =
    useGetQRCodeDataQuery({
      esimId: esimId,
    });

  const { data: packageData } = useGetPackageQuery(
    { id: packageId! },
    { skip: !packageId }
  );

  const { isLoading: isQRCodeLoading } = useGetQRCodeQuery({
    esimId,
  });

  return (
    <ResponsiveModal
      title={t('pages.qrCode.title')}
      headerIcon={false}
      isOpen={isOpen}
      onClose={onClose}
      content={
        <PageWrapper
          loading={isQRCodeLoading || isQRCodeDataLoading}
          rootClassName='space-y-4 text-wl-on-surface'
        >
          {packageData && (
            <PackageCard
              item={packageData}
              className='max-w-full overflow-hidden'
              country={packageData?.countries[0]}
              showPrice={false}
            />
          )}

          <h1>{t('pages.qrCode.description')}</h1>
          <div className='bg-wl-background flex justify-center py-4 rounded-xl'>
            <QRCode esimId={esimId} />
          </div>

          <div className='bg-wl-background relative text-wl-on-background p-3 rounded-xl flex justify-between items-center'>
            <div className='flex-1 mr-4'>
              <p className='mb-1 text-M-16'>{t('pages.qrCode.smdp')}</p>
              <p className='text-R-16 break-all'>{QRCodeData?.smdpAddress}</p>
            </div>
            <CopyButton
              text={QRCodeData?.smdpAddress}
              successMessage={t('notification.success.copied', {
                name: t('pages.qrCode.smdp'),
              })}
            />
          </div>
          <div className='bg-wl-background relative text-wl-on-background p-3 rounded-xl flex justify-between items-center'>
            <div className='flex-1 mr-4'>
              <p className='mb-1 text-M-16'>
                {t('pages.qrCode.iosActivationCode')}
              </p>
              <p className='text-R-16 break-all'>
                {QRCodeData?.activationCode}
              </p>
            </div>
            <CopyButton
              text={QRCodeData?.activationCode}
              successMessage={t('notification.success.copied', {
                name: t('pages.qrCode.iosActivationCode'),
              })}
            />
          </div>
          <div className='bg-wl-background relative text-R-16 text-wl-on-background p-3 rounded-xl flex justify-between items-center'>
            <div className='flex-1 mr-4'>
              <p className='mb-1 text-M-16'>
                {t('pages.qrCode.androidActivationCode')}
              </p>
              <p className='text-R-16 break-all'>{QRCodeData?.fullCode}</p>
            </div>
            <CopyButton
              text={QRCodeData?.fullCode}
              successMessage={t('notification.success.copied', {
                name: t('pages.qrCode.androidActivationCode'),
              })}
            />
          </div>
        </PageWrapper>
      }
      footer={
        <PageSection>
          <Button
            className='btn-secondary w-full h-auto py-3 text-M-16'
            onClick={onClose}
          >
            Close
          </Button>
        </PageSection>
      }
    />
  );
}
